/**
 * V6.1.A Voice GM (2026-06-02) — persona presets.
 *
 * Curated narrator personas mapped to verified Kokoro voice IDs (catalog
 * confirmed on dallas via /api/voice/voices?engine=kokoro). The TalkBar
 * persona picker exposes these so the player can swap GM voice mid-session
 * without opening the Audio drawer.
 *
 * Selecting a persona writes its voice id via useNarratorVoice — the
 * existing localStorage hook + custom event already keeps every consumer
 * in sync (VoicePickerSection, ReadAloudControl, useTtsQueue, etc.).
 */
import { useCallback } from 'react';
import { useNarratorVoice, NARRATOR_VOICE_DEFAULT } from './useNarratorVoice';

export interface PersonaPreset {
  /** Stable ordering key + radio/select value. */
  id: string;
  /** Short display label (≤ 12 chars to fit in the TalkBar chip). */
  label: string;
  /** Verified Kokoro voice id (catalog-checked 2026-06-02 on dallas). */
  voiceId: string;
  /** Sub-text shown in the dropdown option for context. */
  description: string;
}

export const PERSONA_PRESETS: ReadonlyArray<PersonaPreset> = [
  {
    id: 'm',
    label: 'M',
    voiceId: 'bm_lewis',
    description: 'British male — default Admiral M',
  },
  {
    id: 'bond',
    label: 'Bond',
    voiceId: 'bm_george',
    description: 'British male — 007',
  },
  {
    id: 'q',
    label: 'Q',
    voiceId: 'bm_daniel',
    description: 'British male — Q Branch chief',
  },
  {
    id: 'cinematic',
    label: 'Cinematic',
    voiceId: 'am_michael',
    description: 'American male — trailer narrator',
  },
  {
    id: 'femme',
    label: 'Femme',
    voiceId: 'bf_emma',
    description: 'British female — Moneypenny',
  },
];

/** Find the preset whose voiceId matches a given Kokoro id, or undefined. */
export function findPresetByVoiceId(voiceId: string): PersonaPreset | undefined {
  return PERSONA_PRESETS.find((p) => p.voiceId === voiceId);
}

/**
 * useTalkBarPersona binds the persona picker UI to useNarratorVoice.
 *
 * Returns the current persona id (or 'custom' when the persisted voice is
 * not one of the 5 presets — e.g. user picked `bf_lily` via the Audio
 * drawer), plus a setter that maps preset id → voice id and writes it
 * through.
 *
 * Wrapping useNarratorVoice keeps the V1 hook authoritative; the TalkBar
 * does NOT introduce a second storage key.
 */
export function useTalkBarPersona(): {
  currentPersonaId: string;
  setPersona: (presetId: string) => void;
  resetToDefault: () => void;
  currentVoiceId: string;
} {
  const [voice, setVoice] = useNarratorVoice();
  const match = findPresetByVoiceId(voice);
  const currentPersonaId = match ? match.id : 'custom';
  const setPersona = useCallback(
    (presetId: string) => {
      const preset = PERSONA_PRESETS.find((p) => p.id === presetId);
      if (preset === undefined) {
        return;
      }
      setVoice(preset.voiceId);
    },
    [setVoice],
  );
  const resetToDefault = useCallback(() => {
    setVoice(NARRATOR_VOICE_DEFAULT);
  }, [setVoice]);
  return {
    currentPersonaId,
    setPersona,
    resetToDefault,
    currentVoiceId: voice,
  };
}
